/**
 * 全局脏标记表 —— 实时事件与页面刷新之间的缓冲区。
 *
 * 设计要点：
 *   1. 合并窗口：markDirty() 在 mergeWindowMs（默认 500ms）内的多次调用只在窗口结束时
 *      通知一次订阅者，避免同一批写库操作引发 N 次页面刷新。
 *   2. 每个消费者各自持有游标：事件历史只追加、不因某个页面消费而删除。
 *      这样 scope:'all' 能同时命中多个已打开的 keep-alive 页签，
 *      而每个页面只按自己的游标判断「我这边有没有新事件」。
 *   3. 隐藏页签不消费游标，因此切回时仍能读到期间积攒的事件并刷新一次。
 *
 * 事件历史只保留最近 MAX_DIRTY_HISTORY 条；游标落后到已被淘汰的区间时，
 * consumeDirty() 返回 truncated=true，调用方应视为「有变化」直接刷新。
 */
import { useSyncExternalStore } from 'react';
import { realtimeConfig } from './config';
import type { RealtimeChangeEvent } from './eventTypes';

interface DirtyRecord {
  seq: number;
  event: RealtimeChangeEvent;
}

export interface DirtyConsumeResult {
  /** 自 sinceSeq 之后的事件 */
  events: RealtimeChangeEvent[];
  /** 当前最新序号，调用方应把它写回自己的游标 */
  seq: number;
  /** sinceSeq 之后已有事件被淘汰，无法精确判断，建议直接刷新 */
  truncated: boolean;
}

const MAX_DIRTY_HISTORY = 200;

let seq = 0;
let version = 0;
let history: DirtyRecord[] = [];
let pending: RealtimeChangeEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => {
    listener();
  });
}

function flush(): void {
  flushTimer = null;
  if (pending.length === 0) return;
  const batch = pending;
  pending = [];
  for (const event of batch) {
    history.push({ seq: ++seq, event });
  }
  if (history.length > MAX_DIRTY_HISTORY) {
    history = history.slice(-MAX_DIRTY_HISTORY);
  }
  version = seq;
  notify();
}

/** 记录一条变更事件（会参与合并窗口）。 */
export function markDirty(event: RealtimeChangeEvent): void {
  if (!event) return;
  pending.push(event);
  if (flushTimer === null) {
    flushTimer = setTimeout(flush, realtimeConfig.mergeWindowMs);
  }
}

/**
 * 读取自 sinceSeq 之后的事件。不修改全局历史，只返回当前游标；
 * 各订阅者用自己的 cursor 调用即可互不干扰。
 */
export function consumeDirty(sinceSeq: number): DirtyConsumeResult {
  const truncated = history.length > 0 && sinceSeq < history[0].seq - 1;
  return {
    events: history
      .filter((record) => record.seq > sinceSeq)
      .map((record) => record.event),
    seq,
    truncated,
  };
}

/** 当前版本号（每次合并窗口结束递增）。供 useSyncExternalStore 使用。 */
export function getDirtyVersion(): number {
  return version;
}

export function subscribeDirty(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * 清空脏标记（登出、退出实时模式时调用）。
 * 序号归零，尚在合并窗口内的事件一并丢弃；订阅者收到通知后会把游标回退到 0。
 */
export function resetDirtyStore(): void {
  pending = [];
  history = [];
  seq = 0;
  version = 0;
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  notify();
}

/** 组件侧订阅版本号；版本变化即表示「有事件已从合并窗口释放」。 */
export function useDirtyVersion(): number {
  return useSyncExternalStore(subscribeDirty, getDirtyVersion, () => 0);
}
